import React, { useState, useEffect } from 'react';
import { FileText, Calendar, ChevronDown, ChevronUp, ZoomIn, X, Loader2, Sparkles } from 'lucide-react';
import api from '../../services/apiService';

const categoryConfig = {
  feature:     { label: 'New Feature',   cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  improvement: { label: 'Improvement',   cls: 'bg-brand-50 text-brand-600 border border-brand-200'       },
  bugfix:      { label: 'Bug Fix',       cls: 'bg-rose-50 text-rose-600 border border-rose-200'          },
  other:       { label: 'Other',         cls: 'bg-slate-100 text-slate-600'                               },
};

const categoryOptions = [
  { value: '',            label: 'All types'   },
  { value: 'feature',    label: 'New Feature' },
  { value: 'improvement',label: 'Improvement' },
  { value: 'bugfix',     label: 'Bug Fix'     },
  { value: 'other',      label: 'Other'       },
];

const isNewEntry = (dateString) => {
  const entryDate = new Date(dateString);
  const weekAgo   = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return entryDate > weekAgo;
};

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('pl-PL', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

const ChangesList = () => {
  const [changes,     setChanges]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);
  const [expandedId,  setExpandedId]  = useState(null);
  const [page,        setPage]        = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [imageModal,  setImageModal]  = useState({ open: false, src: '', title: '' });
  const [category,    setCategory]    = useState('');

  const fetchChanges = async (pageNum = 1, append = false, cat = category) => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      let url = `/changelogs/?page=${pageNum}`;
      if (cat) url += `&category=${cat}`;
      const { data } = await api.get(url);
      append
        ? setChanges(prev => [...prev, ...data.results])
        : setChanges(data.results || []);
      setTotalCount(data.count || 0);
      setHasMore(!!data.next);
      setPage(pageNum);
    } catch {
      setError('Failed to load changes.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchChanges(1, false, category); }, [category]); // eslint-disable-line

  return (
    <div className="flex flex-col bg-white h-full min-h-0">

      {/* ── header ── */}
      <div className="flex items-center justify-between px-5 pr-12 pt-5 pb-4 border-b border-steel-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600">
            <FileText size={15} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800 leading-tight">Changelog</h2>
            <p className="text-xs text-slate-400">Recent updates &amp; improvements</p>
          </div>
        </div>
        <select
          value={category}
          onChange={e => { setCategory(e.target.value); setPage(1); }}
          className="text-xs px-3 py-1.5 border border-steel-200 rounded-lg bg-white text-slate-600
                     focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400"
        >
          {categoryOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* ── body ── */}
      <div className="flex-1 overflow-y-auto px-5 py-4">

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center py-16 text-sm text-rose-400">{error}</div>
        )}

        {!loading && !error && changes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-300">
            <FileText size={32} />
            <span className="text-sm">No changes to display</span>
          </div>
        )}

        {!loading && !error && changes.length > 0 && (
          <div className="space-y-2.5">
            {changes.map((change) => {
              const catCfg  = categoryConfig[change.category] || categoryConfig.other;
              const isNew   = isNewEntry(change.created_at);
              const isOpen  = expandedId === change.id;

              return (
                <div
                  key={change.id}
                  className="border border-steel-200 rounded-xl overflow-hidden hover:border-steel-300 transition-colors"
                >
                  {/* row header */}
                  <div
                    className="px-4 py-3 cursor-pointer hover:bg-steel-50 transition-colors"
                    onClick={() => setExpandedId(isOpen ? null : change.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
                          {isNew && (
                            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
                                             bg-amber-50 text-amber-600 border border-amber-200">
                              <Sparkles size={8} />
                              NEW
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${catCfg.cls}`}>
                            {change.category_display || catCfg.label}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <Calendar size={9} />
                            {formatDate(change.created_at)}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold text-slate-800 leading-snug truncate">
                          {change.title}
                        </h3>
                      </div>
                      <div className="flex-shrink-0 text-slate-300 mt-0.5">
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                  </div>

                  {/* expanded content */}
                  {isOpen && (
                    <div className="px-4 pb-4 pt-3 border-t border-steel-100 bg-steel-50/40">
                      <div className="flex items-start gap-3">
                        <p className="flex-1 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                          {change.description}
                        </p>
                        {change.image && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setImageModal({ open: true, src: change.image, title: change.title });
                            }}
                            className="flex-shrink-0 p-1.5 rounded-lg text-brand-500 hover:bg-brand-50 transition-colors"
                            title="View image"
                          >
                            <ZoomIn size={16} />
                          </button>
                        )}
                      </div>
                      {change.image && (
                        <button
                          className="mt-3 w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageModal({ open: true, src: change.image, title: change.title });
                          }}
                        >
                          <img
                            src={change.image}
                            alt={change.title}
                            className="w-full rounded-lg border border-steel-200 object-cover max-h-48 hover:opacity-90 transition-opacity"
                          />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {hasMore && (
              <div className="pt-2 text-center">
                <button
                  onClick={() => fetchChanges(page + 1, true)}
                  disabled={loadingMore}
                  className="text-xs px-5 py-2 rounded-lg border border-steel-200 bg-white text-slate-500
                             hover:bg-steel-50 hover:border-brand-300 hover:text-brand-600 transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loadingMore
                    ? <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" />Loading…</span>
                    : `Show more (${changes.length} of ${totalCount})`
                  }
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── image lightbox ── */}
      {imageModal.open && (
        <div
          className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4"
          onClick={() => setImageModal({ open: false, src: '', title: '' })}
        >
          <div className="relative max-w-5xl max-h-full">
            <button
              onClick={() => setImageModal({ open: false, src: '', title: '' })}
              className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
            >
              <X size={28} />
            </button>
            <img
              src={imageModal.src}
              alt={imageModal.title}
              className="max-w-full max-h-[85vh] rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangesList;
